import { useRef, useState } from "react";
import { createOpaqueKey, type ReportReason } from "../game/api";
import { useModalFocus } from "../hooks/useModalFocus";

interface ReportDialogProps {
  opponentName: string;
  onSubmit: (reason: ReportReason, details: string, idempotencyKey: string) => Promise<void>;
  onClose: () => void;
}

const REASONS: Array<{ value: ReportReason; label: string }> = [
  { value: "cheating", label: "Cheating or automation" },
  { value: "abuse", label: "Abusive conduct" },
  { value: "harassment", label: "Harassment" },
  { value: "name", label: "Inappropriate player name" },
  { value: "disconnect", label: "Intentional disconnect" },
  { value: "exploit", label: "Exploit abuse" },
  { value: "collusion", label: "Collusion" },
];

export function ReportDialog({ opponentName, onSubmit, onClose }: ReportDialogProps) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const [reason, setReason] = useState<ReportReason>("cheating");
  const [details, setDetails] = useState("");
  const [key] = useState(createOpaqueKey);
  const [status, setStatus] = useState<"editing" | "submitting" | "success" | "error">("editing");
  const [error, setError] = useState<string | null>(null);
  useModalFocus(dialogRef, true, status === "submitting" ? () => undefined : onClose);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setStatus("submitting");
    setError(null);
    try {
      await onSubmit(reason, details, key);
      setStatus("success");
    } catch (submitError) {
      setStatus("error");
      setError(submitError instanceof Error ? submitError.message : "The report could not be submitted.");
    }
  };

  return (
    <div className="modal-backdrop" role="presentation">
      <div ref={dialogRef} className="confirm-dialog report-dialog" role="dialog" aria-modal="true" aria-labelledby="report-title" tabIndex={-1}>
        {status === "success" ? (
          <>
            <p className="eyebrow"><span /> Report received</p>
            <h2 id="report-title">Thanks for the signal</h2>
            <p>Your report about {opponentName} was submitted once for this match.</p>
            <button className="primary-button" type="button" onClick={onClose}>Done</button>
          </>
        ) : (
          <form onSubmit={(event) => void submit(event)}>
            <p className="eyebrow"><span /> Post-match safety</p>
            <h2 id="report-title">Report {opponentName}</h2>
            <p>Choose the closest reason. Reports are limited to verified opponents from matches you played.</p>
            <label className="report-field">
              <span>Reason</span>
              <select value={reason} onChange={(event) => setReason(event.target.value as ReportReason)} disabled={status === "submitting"}>
                {REASONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <label className="report-field">
              <span>Details <small>optional</small></span>
              <textarea
                value={details}
                onChange={(event) => setDetails(event.target.value.slice(0, 1_000))}
                maxLength={1_000}
                rows={4}
                disabled={status === "submitting"}
                placeholder="Share only what helps review this match."
              />
            </label>
            {error && <p className="form-error" role="alert">{error}</p>}
            <div className="report-actions">
              <button className="text-button" type="button" onClick={onClose} disabled={status === "submitting"}>Cancel</button>
              <button className="danger-button" type="submit" disabled={status === "submitting"}>
                {status === "submitting" ? "Submitting…" : "Submit report"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
